/**
 * Embedded canisterId → displayName map (build-time catalog).
 * Filled by refresh-collection-catalog.mjs from Entrepot / NFTGeek / DAB / DGDG / Bazaar.
 */
import { ENTREPOT_PROXY } from "./constants.js";
import { normalizeIcpCanisterId } from "./icAgent.js";
import catalogJson from "./data/icpCollectionCanisters.generated.js";
function asCatalog(raw) {
    if (raw && typeof raw === "object")
        return raw;
    return {};
}
let cached = null;
function loadMap() {
    if (cached)
        return cached;
    const file = asCatalog(catalogJson);
    const map = new Map();
    for (const [rawId, rawName] of Object.entries(file.names ?? {})) {
        const id = normalizeIcpCanisterId(rawId);
        const name = typeof rawName === "string" ? rawName.trim() : "";
        if (!id || id === ENTREPOT_PROXY || name.length < 2)
            continue;
        if (/^[a-z0-9]{5}-[a-z0-9]{5}-[a-z0-9]{5}-[a-z0-9]{5}-cai$/i.test(name)) {
            continue;
        }
        map.set(id, name);
    }
    cached = map;
    return map;
}
/** All known display names from the embedded catalog. */
export function getEmbeddedCollectionNames() {
    return loadMap();
}
/** Lookup a human collection title for a canister (null if unknown). */
export function lookupEmbeddedCollectionName(canisterId) {
    const id = normalizeIcpCanisterId(canisterId);
    if (!id)
        return null;
    return loadMap().get(id) ?? null;
}
/**
 * Best display label for a collection canister.
 * Prefer explicit name → embedded map → short principal.
 */
export function resolveCollectionDisplayName(canisterId, explicit) {
    const trimmed = explicit?.trim();
    if (trimmed && trimmed.length >= 2)
        return trimmed;
    const mapped = lookupEmbeddedCollectionName(canisterId);
    if (mapped)
        return mapped;
    const id = normalizeIcpCanisterId(canisterId) ?? canisterId;
    return id.length > 14 ? `${id.slice(0, 12)}…` : id;
}
/**
 * Live probe EXT / ICRC-7 collection name when the embedded map misses.
 * Results are not persisted — callers may cache in UI state.
 */
export async function probeCollectionName(canisterId) {
    const id = normalizeIcpCanisterId(canisterId);
    if (!id)
        return null;
    const known = lookupEmbeddedCollectionName(id);
    if (known)
        return known;
    try {
        const { Actor } = await import("@dfinity/agent");
        const { IDL } = await import("@dfinity/candid");
        const { createIcpAgent } = await import("./icAgent.js");
        const agent = await createIcpAgent();
        const actor = Actor.createActor(() => IDL.Service({
            icrc7_name: IDL.Func([], [IDL.Text], ["query"]),
            getName: IDL.Func([], [IDL.Text], ["query"]),
            name: IDL.Func([], [IDL.Text], ["query"]),
            dip721_name: IDL.Func([], [IDL.Text], ["query"]),
        }), { agent, canisterId: id });
        for (const fn of [
            actor.icrc7_name,
            actor.getName,
            actor.name,
            actor.dip721_name,
        ]) {
            if (typeof fn !== "function")
                continue;
            try {
                const n = (await fn()).trim();
                if (n.length >= 2 && !/^[a-z0-9-]{20,}-cai$/i.test(n))
                    return n;
            }
            catch {
                /* try next */
            }
        }
    }
    catch {
        /* unreachable */
    }
    return null;
}
